<template>
  <div
    v-if="metric"
    class="relative h-full rounded-md"
    :class="[
      isOnProspecting ? '' : 'shadow-md',
      hideGraph ? 'shadow-md' : 'shadow-sm',
      {
        'shadow-[#1037FF80]': metric.input_type == 'Number',
        'shadow-[#9747FF80]': metric.input_type == 'Decimal',
        'shadow-[#0D729980]': metric.input_type == 'Percent',
        'shadow-[#45D72D80]': metric.input_type == 'Currency',
        'shadow-[#D1970080]': metric.input_type == 'StarRating',
        'shadow-[#DB00FF80]': metric.input_type == 'BarRating'
      }
    ]"
    @dblclick="$emit('dblclick')"
    @mouseenter="edit_mode = true"
    @mouseleave="edit_mode = false">
    <div
      ref="parentDiv"
      :class="[
        metric_clone?.show_formula || metric_clone?.show_description ? '' : 'overflow-hidden',
        isOnForecasting && 'min-h-[242px] max-h-[242px]',
        isOnProspecting && 'min-h-[112px]',
        isFirstItem ? 'min-h-[148px]' : '',
        metric_clone.show_description || metric_clone.show_formula ? '' : 'p-3',
        [hideGraph ? 'min-h-[126px]' : '']
      ]"
      class="relative flex flex-col justify-between w-full h-full transition ease-in-out rounded-lg bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration">
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0">
        <div
          v-if="showMenu"
          v-click-outside="onClickOutSide"
          class="dark:bg-dark-screen-background absolute z-20 bg-white top-30 right-10 w-[200px] rounded-lg shadow-2xl h-[100px] flex-col justify-center items-start content-center flex text-grey-light">
          <div
            class="flex cursor-pointer justify-center items-center content-center font-normal ml-2 mt-3 mb-6 text-[13px]"
            @click="handleUpdate">
            <Pencil class="w-5 h-5 ml-4" />
            <span class="ml-4 text-grey-light dark:text-dark-body-text">{{ $t("Edit Chart") }}</span>
          </div>
          <div
            class="flex cursor-pointer mb-2 justify-center items-center content-center font-normal ml-2 text-[13px]"
            @click="handleDelete">
            <Trash2 class="w-5 h-5 ml-4" />
            <span class="ml-4 text-grey-light dark:text-dark-body-text">{{ $t("Delete Chart") }}</span>
          </div>
        </div>
      </transition>
      <div
        class="flex items-stretch justify-between w-full"
        :class="[metric_clone.show_description || metric_clone.show_formula ? 'px-3 pt-3' : '']">
        <div
          class="flex justify-between font-bold cursor-default underline-offset-2"
          @click.stop="
            ($event) => {
              editing_disabled ? null : $event?.ctrlKey || $event?.altKey ? openEditMetric(metric_clone) : void null
            }
          "
          @mouseout="$event.target.classList.remove('cursor-pointer', 'underline', 'text-blue')"
          @mouseover="
            ($event) => {
              $event?.ctrlKey || $event?.altKey
                ? $event.target.classList.add('cursor-pointer', 'underline', 'text-blue')
                : $event.target.classList.remove('cursor-pointer', 'underline', 'text-blue')
            }
          ">
          <div class="flex items-center dark:text-dark-body-text">
            <div class="mr-1 -ml-1">
              <NumbersIcon v-if="metric.input_type == 'Number'" class="w-[48px] h-[48px]" />
              <DecimalIcon v-else-if="metric.input_type == 'Decimal'" class="w-[48px] h-[48px]" />
              <PercentIcon v-else-if="metric.input_type == 'Percent'" class="w-[48px] h-[48px]" />
              <CurrencyIcon v-else-if="metric.input_type == 'Currency'" class="w-[48px] h-[48px]" />
              <StarRatingIcon v-else-if="metric.input_type == 'StarRating'" class="w-[48px] h-[48px]" />
              <BarRatingIcon v-else-if="metric.input_type == 'BarRating'" class="w-[48px] h-[48px]" />
            </div>

            <div class="flex has-tooltip">
              <div
                :ref="`rollupTextContainer${metric.id}`"
                v-responsive-text
                :style="{ width: `${divWidth - 120}px` }"
                :class="{ 'truncate overflow-ellipsis ': !inPlaybook }"
                class="flex-1 whitespace-nowrap pt-1">
                {{ metric_label }}
              </div>
              <span
                class="items-center gap-2 px-2 py-1 text-lg font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ metric_clone.description || metric_label }} -
                {{ metric.aggregation_method }}
              </span>
              <span
                class="absolute top-0 left-0 z-50 items-center hidden gap-2 px-2 py-1 text-sm font-medium rounded-md md:inline-flex tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                For:
                {{
                  tied_company
                    ? dataSource == "this_record"
                      ? `
                      ${record_not_found ? $t("Not found") : $utils.getRecordName(tied_company)}`
                      : `${record_not_found ? $t("Record not found") : $utils.getRecordName(tied_company)}`
                    : record_not_found
                      ? $t("Record not found")
                      : $t("All Records")
                }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center">
          <div
            v-if="metric_clone?.metricName != 'arr' && metric_clone?.metricName != 'mrr'"
            class="items-center hidden ml-2 md:flex text-common-grey">
            <Sigma
              v-if="!metric_clone.show_formula"
              class="w-6 h-6 cursor-pointer"
              @click.stop="
                () => {
                  showNestedMetrics()
                  metric_clone.show_description = false
                }
              " />
            <X
              v-else
              class="w-7 h-7 cursor-pointer"
              @click.stop="
                () => {
                  metric_clone.show_formula = false
                  metric_clone.show_description = false
                }
              " />
            <AlignLeft
              v-if="!metric_clone.show_description"
              class="w-6 h-6 cursor-pointer"
              @click.stop="
                () => {
                  metric_clone.show_description = true
                  metric_clone.show_formula = false
                }
              " />
            <Tally2
              v-else
              class="w-6 h-6 mt-2 cursor-pointer rotate-90"
              @click.stop="
                () => {
                  metric_clone.show_description = false
                  metric_clone.show_formula = false
                }
              " />
          </div>
        </div>
      </div>
      <div
        class="box-border relative flex items-center content-center justify-between text-xl font-semibold sm:text-2xl"
        :class="[
          itemLayoutHeight > 6 && !hideGraph && hasValues && !(metric_clone.show_description || metric_clone.show_formula)
            ? 'h-full'
            : 'h-fit',
          metric_clone.show_description || metric_clone.show_formula ? 'px-3' : ''
        ]">
        <div
          :class="[
            { '!-mt-4': percentage_change !== null && !metric_clone.show_formula },
            { 'mt-6': plotted_values.length > 1 && percentage_change !== null }
          ]"
          class="flex items-center content-center gap-1 -mt-4 font-bold">
          <div
            :class="[itemLayoutHeight > 6 ? 'text-5xl' : 'text-4xl -mt-1']"
            class="flex font-bold text-justify truncate text-grey-light dark:text-dark-body-text">
            {{ metric_clone.value === "blank" ? "0" : $utils.humanizeMetricValue(metric_clone, metric_clone.value) }}
          </div>
          <div
            class="flex items-center content-center pl-2 ml-1 rounded-full"
            :class="
              calcaulated_percentage_change === -1
                ? '!bg-status-error-red-transparent'
                : calcaulated_percentage_change === 1
                  ? '!bg-status-success-green-transparent'
                  : '!bg-status-warning-yellow-transparent'
            ">
            <div v-if="metric_clone.value !== 'blank' || calcaulated_percentage_change !== 0">
              <ChevronUp v-if="calcaulated_percentage_change > 0" />
              <ChevronDown v-if="calcaulated_percentage_change < 0" />
            </div>
            <div
              class="pr-2 text-sm text-center"
              :class="
                calcaulated_percentage_change === -1
                  ? '!text-status-error-red'
                  : calcaulated_percentage_change === 1
                    ? '!text-status-success-green'
                    : '!text-status-warning-yellow'
              ">
              {{ percentage_change ? `${percentage_change.toFixed(2)}%` : "" }}
            </div>
          </div>
        </div>
        <div
          v-if="!hideGraph && hasValues"
          :class="[
            isOnProspecting && 'overflow-y-hidden',
            (metric_clone.show_description || metric_clone.show_formula) && itemLayoutHeight > 6
              ? 'h-[90px]'
              : itemLayoutHeight == 6
                ? 'h-[50px]'
                : 'h-[100px]',
            { 'mb-12': !metric_clone.show_formula }
          ]"
          class="relative min-w-[80px] w-full min-h-[46px] ml-2">
          <MetricSummaryChart
            v-if="percentage_change !== null"
            class="relative w-full h-full"
            :percentage-change="parseInt(percentage_change)"
            :is-decrease-positive="metric.metric_colors?.is_decrease_positive"
            :metric-values="plotted_values"
            :chartid="metric_clone.id" />
        </div>
        <div
          v-if="(!hasValues && !hideGraph) || plotted_values.length < 2"
          class="relative hidden mt-8 text-sm font-light text-gray-500 outline-none md:flex dark:text-dark-grey-text">
          {{ $t("No data to display") }}
          <span v-if="metric_clone.errors" class="has-tooltip">
            <CircleAlert class="w-5 h-5 text-status-error-red" />
            <span
              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text -right-0 -top-10">
              {{ $t("Can't compute Metric") }} {{ metric.name }} {{ $t("it has records with wrong values!") }}
            </span>
          </span>
        </div>
      </div>
      <div
        v-show="metric_clone.show_description"
        class="flex z-30 flex-col items-center w-full h-full p-3 bg-white rounded-b-lg shadow-xl"
        :class="[itemLayoutHeight >= 12 ? '-mt-10.5' : '-mt-10 z-10', itemLayoutWidth == 12 ? '' : '-mt-10 ']">
        <span class="text-common-grey min-h-[30px]">{{
          itemLayoutHeight == 6 && metric_clone?.description?.length > 60
            ? metric_clone?.description.slice(0, 60) + "..."
            : itemLayoutHeight > 6 && metric_clone?.description?.length > 100
              ? metric_clone?.description.slice(0, 100) + "..."
              : metric_clone?.description
        }}</span>
        <hr class="w-full my-2 mx-8 h-0.5 bg-gray-200 border-0 dark:bg-gray-700">
        <div class="flex items-center gap-3">
          <div v-if="itemLayoutWidth < 18" class="flex items-center gap-1 has-tooltip mr-2">
            <Eye v-if="metric_clone.is_public" class="w-5 h-5" />
            <EyeOff v-else class="w-5 h-5" />
            <span
              class="items-center gap-2 px-2 py-1 text-lg font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
              {{ metric_clone.is_public ? $t("Public Metric") : $t("Private Metric") }}
            </span>
          </div>
          <div v-else class="flex items-center gap-1">
            <Eye v-if="metric_clone.is_public" class="w-5 h-5 text-common-grey" />
            <EyeOff v-else class="w-5 h-5 text-common-grey" />
            <span class="whitespace-nowrap text-sm">{{ metric_clone.is_public ? $t("Public") : $t("Private") }} {{ itemLayoutWidth > 12 ? $t("Metric") : "" }}</span>
          </div>
          <div v-if="itemLayoutWidth < 18" class="flex items-center gap-1 has-tooltip mr-2">
            <Lock v-if="metric_clone.is_read_only" class="w-5 h-5 text-common-grey" />
            <LockOpen v-else class="w-5 h-5 text-common-grey" />
            <span
              class="items-center gap-2 px-2 py-1 text-lg font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
              {{ $t("Read-Only") }}
            </span>
          </div>
          <div v-else class="flex items-center gap-1">
            <Lock v-if="metric_clone.is_read_only" class="w-5 h-5 text-common-grey" />
            <LockOpen v-else class="w-5 h-5 text-common-grey" />
            <span class="flex flex-wrap whitespace-nowrap text-sm">{{ $t("Read-Only") }}</span>
          </div>
          <div
            v-if="itemLayoutWidth < 18"
            class="flex items-center gap-1 cursor-pointer has-tooltip"
            @click.stop="
              ($event) => {
                editing_disabled ? null : openEditMetric(metric_clone)
              }
            ">
            <ExternalLink class="w-5 h-5 text-common-grey" />
            <span
              class="items-center gap-2 px-2 py-1 text-lg font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
              {{ $t("View more details") }}
            </span>
          </div>
          <div
            v-else
            class="flex items-center gap-1 cursor-pointer"
            @click.stop="
              ($event) => {
                editing_disabled ? null : openEditMetric(metric_clone)
              }
            ">
            <ExternalLink class="w-5 h-5 text-common-grey" />
            <span class="whitespace-nowrap text-sm">{{ itemLayoutWidth > 12 ? $t("View more details") : $t("View") }}</span>
          </div>
        </div>
      </div>

      <div
        v-show="metric_clone.show_formula && metric_clone?.results?.length"
        class="flex flex-wrap w-full p-3 bg-white rounded-b-lg shadow-xl"
        :class="[itemLayoutHeight >= 12 ? '' : '-mt-1 z-30', itemLayoutWidth == 12 ? '' : '-mt-1 z-10']">
        <div />
        <div class="flex w-full mr-2">
          <div
            :class="{
              'pointer-events-none opacity-60': rollup_day_option_value !== 'Q' && rollup_day_option_value !== 'Y'
            }"
            class="flex space-x-2 items-center">
            <div>
              <SelectInput
                v-model="truncate_by"
                :value="truncate_by"
                value-attr="value"
                label-attr="label"
                class="w-[120px]"
                name="truncate_by"
                :input-classes="'text-left'"
                :is-box-background="true"
                :show-label="true"
                :allow-null="false"
                :items="truncate_by_options"
                :label="$t('Truncate by')" />
            </div>
          </div>

          <div class="ml-auto flex space-x-2">
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 7 }"
              @click="() => handleSelectRollupDays(7)">
              1W
            </div>
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 14 }"
              @click="handleSelectRollupDays(14)">
              2W
            </div>
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 21 }"
              @click="() => handleSelectRollupDays(21)">
              3W
            </div>
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 30 }"
              @click="() => handleSelectRollupDays(30)">
              1M
            </div>
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 'Q' }"
              @click="() => handleSelectRollupDays('Q')">
              1Q
            </div>
            <div
              class="cursor-pointer"
              :class="{ 'font-bold underline': rollup_day_option_value == 'Y' }"
              @click="() => handleSelectRollupDays('Y')">
              1Y
            </div>
          </div>
        </div>
        <div class="border-b w-[90%] ml-[5%] h-1.5 mb-5" />

        <div
          v-for="nested_metric in metric_clone.results"
          :key="nested_metric.id"
          class="flex items-center relative content-center px-2 py-1 m-1 bg-gray-200 rounded-full"
          :style="{ backgroundColor: `${nested_metric.backgroundColor}1F` }">
          <div class="flex">
            <span class="mr-1 font-bold" :style="{ color: `${nested_metric.backgroundColor}` }">{{ nested_metric.name }}:</span>
            <span class="mr-1 font-medium">{{
              nested_metric.value === "blank" ? "0" : $utils.humanizeMetricValue(nested_metric, nested_metric.value)
            }}</span>

            <div
              :class="{ 'mr-20': nested_metric.plotted_values?.length > 1 }"
              class="flex items-center pr-2 text-center"
              :style="{ color: `${nested_metric.backgroundColor}` }">
              <Tally2 v-if="nested_metric.percentage_change == 0" class="w-4 h-4 rotate-90" />
              <ChevronUp v-if="nested_metric.percentage_change > 0" class="w-4 h-4" />
              <ChevronDown v-if="nested_metric.percentage_change < 0" class="w-4 h-4" />
              <span class="font-medium">{{
                nested_metric.percentage_change ? `${nested_metric.percentage_change.toFixed(2)}%` : "0%"
              }}</span>
            </div>

            <div v-if="nested_metric.plotted_values?.length > 1" class="h-6 w-16 absolute right-2 top-1 bg-white rounded-full" />
            <div v-if="nested_metric.plotted_values?.length > 1" class="h-12 w-12 absolute right-5 -top-0.5">
              <MetricSummaryChart
                v-if="nested_metric.percentage_change !== null"
                class="relative w-full h-full"
                :percentage-change="nested_metric.percentage_change"
                :is-decrease-positive="false"
                :metric-values="nested_metric.plotted_values"
                :chartid="nested_metric.id" />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!metric_clone.show_formula && !metric_clone.show_description"
        :class="[
          {
            'blue-bottom-shadow': metric.input_type == 'Number',
            'purple-bottom-shadow': metric.input_type == 'Decimal',
            'light-blue-bottom-shadow': metric.input_type == 'Percent',
            'green-bottom-shadow': metric.input_type == 'Currency',
            'yellow-bottom-shadow': metric.input_type == 'StarRating',
            'pink-bottom-shadow': metric.input_type == 'BarRating'
          }
        ]"
        class="absolute -bottom-[130px] overflow w-[100%] h-32 z-20 left-0" />

      <div v-show="!(metric_clone.show_description || metric_clone.show_formula)" class="absolute bottom-3 right-0 w-full h-[4px]">
        <div class="flex items-center justify-center w-full h-full">
          <!-- <div
            class="w-full h-full mx-12 rounded-tl-full rounded-tr-full drop-shadow-2xl blur-lg"
            :class="[{ 'bg-[#1037FF]': metric.input_type == 'Number', 'bg-[#9747FF]': metric.input_type == 'Decimal', 'bg-[#0D7299]': metric.input_type == 'Percent', 'bg-[#45D72D]': metric.input_type == 'Currency', 'bg-[#D19700]': metric.input_type == 'StarRating', 'bg-[#DB00FF]': metric.input_type == 'BarRating' }]"
          /> -->
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="w-full px-4 py-4 overflow-hidden animate-pulse">
      <dt
        class="w-1/2 h-5 overflow-hidden text-lg font-bold text-gray-500 bg-gray-200 rounded-md lg:text-xl whitespace-nowrap overflow-ellipsis" />
      <dd class="flex flex-wrap items-baseline justify-between gap-2 mt-1">
        <div
          class="w-full h-5 overflow-hidden text-xl font-semibold bg-gray-200 rounded-md sm:text-2xl text-blue whitespace-nowrap overflow-ellipsis" />
        <div
          class="w-full h-5 overflow-hidden text-xl font-semibold bg-gray-200 rounded-md sm:text-2xl text-blue whitespace-nowrap overflow-ellipsis" />
      </dd>
    </div>
  </div>
</template>

<script>
import {
  Sigma,
  AlignLeft,
  ChevronDown,
  ChevronUp,
  Tally2,
  Trash2,
  Eye,
  EyeOff,
  Pencil,
  X,
  CircleAlert,
  Lock,
  LockOpen,
  ExternalLink
} from "lucide-vue-next"
import MetricSummaryChart from "../MetricSummaryChart.vue"
import NumbersIcon from "../icons/NumbersIcon.vue"
import DecimalIcon from "../icons/DecimalIcon.vue"
import PercentIcon from "../icons/PercentIcon.vue"
import CurrencyIcon from "../icons/CurrencyIcon.vue"
import StarRatingIcon from "../icons/StarRatingIcon.vue"
import BarRatingIcon from "../icons/BarRatingIcon.vue"
import { PAGE_REFERENCES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import SelectInput from "@/components/shared/SelectInput.vue"

export default {
  components: {
    CircleAlert,
    Tally2,
    ChevronUp,
    Lock,
    LockOpen,
    ExternalLink,
    Eye,
    MetricSummaryChart,
    Pencil,
    Trash2,
    NumbersIcon,
    DecimalIcon,
    PercentIcon,
    CurrencyIcon,
    StarRatingIcon,
    BarRatingIcon,
    Sigma,
    X,
    AlignLeft,
    EyeOff,
    ChevronDown,
    SelectInput
  },
  inject: ["canUserEditLayout"],
  props: {
    rollupDayOptionValue: null,
    truncateBy: null,
    inPlaybook: null,
    metric: {
      type: [Object, String],
      required: true
    },
    linkedRecord: null,
    isOnProspecting: {
      type: Boolean,
      default: false
    },
    isOnForecasting: {
      type: Boolean,
      default: false
    },
    isFirstItem: {
      type: Boolean,
      required: false
    },
    dataSource: {
      type: String,
      default: null
    },

    hideGraph: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    itemLayoutWidth: {
      type: Number,
      default: null
    },
    itemLayoutHeight: {
      type: Number,
      default: null
    },
    showDescription: {
      type: Boolean,
      default: false
    }
  },
  emits: ["dblclick", "update:previewSelections"],
  data() {
    return {
      showMenu: false,
      divWidth: 0,
      truncate_by_options: [
        { value: "day", label: "Day" },
        { value: "month", label: "Month" }
      ],
      truncate_by: "day",
      rollup_day_option_value: "Y",
      plotted_values: [],
      percentage_change: null,
      calcaulated_percentage_change: null,
      edit_mode: false,
      metric_clone: null,
      tied_company: null,
      record_not_found: false
    }
  },
  computed: {
    metric_label() {
      return this.label ? this.label : this.metric_clone?.label || this.metric_clone.name
    },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },

    hasValues() {
      return this.metric_clone.history?.length > 0
    }
  },
  watch: {
    itemLayoutWidth() {
      this.getDivWidth()
    },
    "metric.value": {
      handler() {
        this.initSummary(this.metric)
      }
    },

    rollup_day_option_value: {
      handler() {
        if (this.rollup_day_option_value !== "Q" && this.rollup_day_option_value !== "Y") {
          if (this.truncate_by == "day") {
            this.updatePreviewSelections()
          }
          this.truncate_by = "day"
        } else {
          this.updatePreviewSelections()
        }
      }
    },
    truncate_by: {
      handler() {
        this.updatePreviewSelections()
        this.$emitter.emit(ALL_EMITTERS.ROLLUP_TRUNCATE_BY_CHANGE, {
          metric: this.metric,
          truncate_by: this.truncate_by
        })
      }
    },

    percentage_change() {
      if (this.metric.metric_colors?.is_decrease_positive) {
        this.calcaulated_percentage_change = -Math.sign(this.percentage_change)
      } else {
        this.calcaulated_percentage_change = Math.sign(this.percentage_change)
      }
    }
  },
  updated() {
    this.getDivWidth()
  },
  created() {
    this.register_listeners()
    if (this.dataSource == "this_record") {
      this.tied_company = this.$store.state.ui.currentCompany
    } else if (this.dataSource == "another_record") {
      if (this.$store.state.data.records_fetched[this.linkedRecord?.id ? this.linkedRecord.id : this.linkedRecord]) {
        this.tied_company = this.$store.state.data.records_fetched[this.linkedRecord.id ? this.linkedRecord.id : this.linkedRecord]
      } else {
        this.$store.dispatch("data/getRecord", this.linkedRecord?.id ? this.linkedRecord.id : this.linkedRecord).then((res) => {
          this.tied_company = res.data
        })
      }
    }

    this.initSummary(this.metric)

    if (this.truncateBy) {
      this.truncate_by = this.truncateBy
    }
    if (this.rollupDayOptionValue) {
      this.rollup_day_option_value = this.rollupDayOptionValue
    }
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    getDivWidth() {
      const parentDiv = this.$refs.parentDiv
      this.divWidth = parentDiv.clientWidth
    },
    updatePreviewSelections() {
      this.$emit("update:previewSelections", {
        rollupDayOptionValue: this.rollup_day_option_value,
        truncateBy: this.truncate_by
      })
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.REFRESH_ROLLUP_METRIC, ({ metric, data_source = null }) => {
        // not solid metric
        if (Object.hasOwn(metric, "id")) {
          if (
            this.isOnProspecting &&
            this.metric?.id == null &&
            (metric.reference_name === this.metric || metric.reference_name === this.metric.reference_name)
          ) {
            this.metric_clone = metric
            this.initSummary(metric)
          } else {
            if (
              (metric.reference_name === this.metric || metric.reference_name === this.metric.reference_name) &&
              (this.dataSource == "another_record" ? this.linkedRecord == data_source : this.dataSource == data_source)
            ) {
              this.metric_clone = metric
              this.initSummary(metric)
            } else if (metric.reference_name === this.metric || metric.reference_name === this.metric.reference_name) {
              if (this.dataSource == "this_record") {
                if (this.$store.state.ui.currentCompany?.id == data_source) {
                  this.metric_clone = metric
                  this.initSummary(metric)
                } else {
                  setTimeout(() => {
                    this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRIC, {
                      metric,
                      data_source
                    })
                  }, 500)
                }
              }
            }
          }
        }
      })
    },
    unregister_listeners() {
      // this.$emitter.off(ALL_EMITTERS.REFRESH_ROLLUP_METRIC)
    },
    initSummary(metric) {
      if (typeof metric === "string") {
        this.metric_clone = utils.getMetricByReferenceName(metric)
      } else {
        this.metric_clone = metric
      }
      this.metric_clone["description"] = this.$store.state.metrics.find((m) => m.id == metric.id)?.description
      if (this.showDescription && this.itemLayoutHeight > 6) {
        this.metric_clone["show_description"] = true
      } else {
        this.metric_clone["show_description"] = false
      }
      this.metric_clone["show_formula"] = false

      this.plotted_values = []
      this.metric_clone.history?.map((val) => {
        this.plotted_values.push(val)
      })
      if (this.plotted_values.length && this.plotted_values[0]?.value != 0) {
        this.percentage_change = metric.percentage_change
      } else {
        this.percentage_change == null
      }
    },
    showNestedMetrics() {
      this.metric_clone?.results?.map((nested_metric) => {
        nested_metric["metric_colors"] = this.$store.state.metrics.find((m) => m.id == nested_metric.id)?.metric_colors
        let plotted_values = []
        nested_metric.history.map((val) => {
          var given = this.$moment(val.datetime, "YYYY-MM-DD")
          var current = this.$moment().startOf("day")
          const days_old = this.$moment.duration(given.diff(current)).asDays() * -1

          let rollup_day_option_num_value = this.rollup_day_option_value

          if (this.rollup_day_option_value == "Y") {
            rollup_day_option_num_value = 365
          }
          if (this.rollup_day_option_value == "Q") {
            const quarterStart = this.$moment().startOf("quarter")
            const given = this.$moment(val.datetime, "YYYY-MM-DD")
            rollup_day_option_num_value = quarterStart.diff(given, "days") * -1
          }

          if (days_old <= rollup_day_option_num_value) {
            plotted_values.push(val)
          }
        })
        nested_metric.percentage_change = null

        if (plotted_values.length && plotted_values[0]?.value != 0) {
          nested_metric.percentage_change =
            ((plotted_values[plotted_values.length - 1]?.value - plotted_values[0]?.value) / plotted_values[0]?.value) * 100
        } else {
          nested_metric.percentage_change == null
        }
        const isNegative = Math.sign(nested_metric.percentage_change)
        const GreenBorder = "#27C884"
        const RedBorder = "#F05B5B"
        const YellowBorder = "#FFBE15"
        if (isNegative === -1) {
          nested_metric["backgroundColor"] = [this.metric.metric_colors?.is_decrease_positive ? GreenBorder : RedBorder]
        } else if (isNegative === 1) {
          nested_metric["backgroundColor"] = [this.metric.metric_colors?.is_decrease_positive ? RedBorder : GreenBorder]
        } else if (isNegative === 0) {
          nested_metric["backgroundColor"] = [YellowBorder]
        }
        nested_metric.plotted_values = plotted_values
      })
      this.metric_clone.show_formula = true
    },
    handleSelectRollupDays(e) {
      this.rollup_day_option_value = e
      this.plotted_values = []

      this.metric_clone?.history?.map((val) => {
        let days_old

        if (typeof e === "number") {
          // If 'e' is a number, calculate days_old based on the number of days
          const given = this.$moment(val.datetime, "YYYY-MM-DD")
          const current = this.$moment().startOf("day")
          days_old = this.$moment.duration(given.diff(current)).asDays() * -1
        } else if (e === "Q") {
          // If 'e' is 'Q', calculate days_old for the current quarter
          const quarterStart = this.$moment().startOf("quarter")
          const given = this.$moment(val.datetime, "YYYY-MM-DD")
          days_old = quarterStart.diff(given, "days")
        }

        if ((typeof e === "number" && days_old <= e) || (e === "Q" && days_old <= 0) || e === "Y") {
          this.plotted_values.push(val)
        }
      })

      this.percentage_change = null

      setTimeout(() => {
        if (this.plotted_values.length && this.plotted_values[0]?.value !== 0) {
          this.percentage_change =
            ((this.plotted_values[this.plotted_values.length - 1].value - this.plotted_values[0].value) / this.plotted_values[0].value) *
            100
        } else {
          this.percentage_change = null
        }
        this.edit_mode = false
        this.showNestedMetrics()
      }, 200)
    },
    onClickOutSide() {
      this.showMenu = false
    },
    handleDelete() {
      this.$emitter.emit(`deleteMetric`, {
        metric: this.metric_clone,
        page_reference: this.isOnProspecting
          ? PAGE_REFERENCES.PROSPECTING
          : this.isOnForecasting
            ? PAGE_REFERENCES.FORECASTING
            : PAGE_REFERENCES.PORTFOLIO
      })
      this.showMenu = false
    },
    openEditMetric() {
      if (this.metric.metric_class == "computed_metric") {
        this.$emitter.emit(ALL_EMITTERS.EDIT_COMPUTED_METRIC, {
          metric: this.metric
        })
      } else {
        this.$emitter.emit(ALL_EMITTERS.EDIT_CUSTOM_METRIC, {
          metric: this.metric
        })
      }
    },

    handleUpdate() {
      this.showMenu = false
    }
  }
}
</script>

<style scoped>
.blue-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #1037ff80;
}

.purple-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #9747ff50;
}

.light-blue-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #0d729950;
}

.green-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #45d72d50;
}

.yellow-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #d1970050;
}

.pink-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #db00ff50;
}
</style>
